import { useEffect, useRef, useState, useCallback } from "react";

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

const normalize = (text: string) => text.toLowerCase().replace(/[^\w\s']/g, "").trim();

interface MicrophoneDevice {
  deviceId: string;
  label: string;
}

export const useMicrophoneCheck = (sentence: string, onStatusChange: (isComplete: boolean) => void) => {
  const sentenceWords = sentence.split(" ");
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const currentWordIndexRef = useRef<number>(0);
  const [verifiedWords, setVerifiedWords] = useState<boolean[]>(Array(sentenceWords.length).fill(false));
  const [isListening, setIsListening] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [showAudioError, setShowAudioError] = useState(false);
  const [showMicErrorModal, setShowMicErrorModal] = useState(false);
  const [microphones, setMicrophones] = useState<MicrophoneDevice[]>([]);
  const [selectedMic, setSelectedMic] = useState<string>(() => {
    if (typeof window === "undefined") {
      return "";
    }
    try {
      return localStorage.getItem("selectedMic") || "";
    } catch {
      return "";
    }
  });
  const recognitionRef = useRef<any>(null);
  const audioTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    currentWordIndexRef.current = currentWordIndex;
  }, [currentWordIndex]);

  const clearAudioTimeout = useCallback(() => {
    if (audioTimeoutRef.current) {
      clearTimeout(audioTimeoutRef.current);
      audioTimeoutRef.current = null;
    }
  }, []);

  const startAudioTimeout = useCallback(() => {
    clearAudioTimeout();
    audioTimeoutRef.current = setTimeout(() => {
      setShowAudioError(true);
    }, 5000);
  }, [clearAudioTimeout]);

  const resetAudioTimeout = useCallback(() => {
    setShowAudioError(false);
    clearAudioTimeout();
    startAudioTimeout();
  }, [clearAudioTimeout, startAudioTimeout]);

  const getMicrophones = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter((device) => device.kind === "audioinput");
      const micDevices: MicrophoneDevice[] = audioInputs.map((device) => ({
        deviceId: device.deviceId,
        label: device.label || `Microphone ${device.deviceId.slice(0, 8)}...`,
      }));
      setMicrophones(micDevices);

      const storedMic = (() => {
        if (typeof window === "undefined") {
          return "";
        }
        try {
          return localStorage.getItem("selectedMic") || "";
        } catch {
          return "";
        }
      })();

      const hasStoredMic = Boolean(storedMic && micDevices.some(({ deviceId }) => deviceId === storedMic));
      const selectedMicExists = Boolean(selectedMic && micDevices.some(({ deviceId }) => deviceId === selectedMic));

      if (hasStoredMic) {
        if (selectedMic !== storedMic) {
          setSelectedMic(storedMic);
        }
      } else if (!selectedMicExists && micDevices.length > 0) {
        const fallbackId = micDevices[0].deviceId;
        setSelectedMic(fallbackId);
        if (typeof window !== "undefined") {
          try {
            localStorage.setItem("selectedMic", fallbackId);
          } catch {
            /* ignore */
          }
        }
      }
    } catch (error) {
      setMicrophones([]);
    }
  }, [selectedMic]);

  useEffect(() => {
    getMicrophones();
  }, [getMicrophones]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
    clearAudioTimeout();
    setShowAudioError(false);
  }, [clearAudioTimeout]);

  const handleMicChange = useCallback(
    (micId: string) => {
      setSelectedMic(micId);
      if (typeof window !== "undefined") {
        try {
          localStorage.setItem("selectedMic", micId);
        } catch {
          /* ignore */
        }
      }
      setCurrentWordIndex(0);
      setVerifiedWords(Array(sentenceWords.length).fill(false));
      setIsComplete(false);
      setShowAudioError(false);

      if (isListening) {
        stopListening();
      }
    },
    [isListening, sentenceWords.length, stopListening],
  );

  const retryListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    setCurrentWordIndex(0);
    currentWordIndexRef.current = 0;
    setVerifiedWords(Array(sentenceWords.length).fill(false));
    setIsComplete(false);
    setIsListening(false);
    setIsInitializing(false);
    setShowAudioError(false);
    clearAudioTimeout();
  }, [sentenceWords.length, clearAudioTimeout]);

  const startListening = useCallback(async () => {
    if (!("webkitSpeechRecognition" in window) && !("SpeechRecognition" in window)) {
      alert("Speech Recognition not supported in this browser");
      return;
    }

    setIsInitializing(true);
    setShowAudioError(false);

    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    try {
      let stream;
      if (selectedMic) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: { deviceId: { exact: selectedMic } },
          });
        } catch (specificMicError) {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        }
      } else {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }

      stream.getTracks().forEach((track) => track.stop());
    } catch (error) {
      setIsInitializing(false);
      setShowMicErrorModal(true);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.lang = "en-US";
    recognition.continuous = true;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setIsListening(true);
      setIsInitializing(false);
      setShowAudioError(false);
      startAudioTimeout();
    };

    recognition.onresult = (event: any) => {
      resetAudioTimeout();
      setShowAudioError(false);

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcript = event.results[i][0].transcript.trim();
        const normalizedTranscript = normalize(transcript);
        const newVerifiedWords = [...verifiedWords];
        let matchedUpToIndex = currentWordIndexRef.current;

        for (let j = currentWordIndexRef.current; j < sentenceWords.length; j += 1) {
          const normalizedWord = normalize(sentenceWords[j]);
          if (normalizedTranscript.includes(normalizedWord)) {
            newVerifiedWords[j] = true;
            matchedUpToIndex = j + 1;
          }
        }

        setVerifiedWords(newVerifiedWords);
        setCurrentWordIndex(matchedUpToIndex);
        currentWordIndexRef.current = matchedUpToIndex;

        if (matchedUpToIndex === sentenceWords.length) {
          recognition.stop();
          setIsComplete(true);
          onStatusChange(true);
        }
      }
    };

    recognition.onerror = (event: any) => {
      setIsListening(false);
      setIsInitializing(false);
      clearAudioTimeout();
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        setShowMicErrorModal(true);
      } else if (event.error === "no-speech") {
        setShowAudioError(true);
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      setIsInitializing(false);
      clearAudioTimeout();
    };

    try {
      recognition.start();
    } catch (error) {
      setIsListening(false);
      setIsInitializing(false);
      clearAudioTimeout();
    }
  }, [selectedMic, sentenceWords, startAudioTimeout, resetAudioTimeout, verifiedWords, onStatusChange, clearAudioTimeout]);

  const renderSentence = useCallback(() => {
    return sentenceWords.map((word, index) => ({
      word,
      className: `font-ubuntu text-[18px] ${
        verifiedWords[index] ? "text-[#1FD167]" : index === currentWordIndex ? "text-black" : "text-grey-4"
      }`,
      isLast: index === sentenceWords.length - 1,
    }));
  }, [sentenceWords, verifiedWords, currentWordIndex]);

  useEffect(() => {
    return () => {
      stopListening();
      if (recognitionRef.current) {
        recognitionRef.current.onend = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.stop();
      }
      clearAudioTimeout();
    };
  }, [stopListening, clearAudioTimeout]);

  useEffect(() => {
    onStatusChange(isComplete);
  }, [isComplete, onStatusChange]);

  return {
    isListening,
    isInitializing,
    isComplete,
    showAudioError,
    showMicErrorModal,
    setShowMicErrorModal,
    startListening,
    retryListening,
    renderSentence,
    microphones,
    selectedMic,
    handleMicChange,
  };
};
